import { useEffect, useState } from "react";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import { FileExcelOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/Tables/GenericTable";
import garageMoneyIcon from "../../../Assets/Icons/garage_money.svg";
import declinedIcon from "../../../Assets/Icons/verified_off.svg";
import paymentIcon from "../../../Assets/Icons/black_payments.svg";
import { useNavigate } from "react-router-dom";
import DateFilter from "../../../Components/DateFilter";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import { Button, Tooltip, message } from "antd";
import * as XLSX from "xlsx";
import { getManagerTransactions } from "../../../API/FleetManager/Transactions";
import { getInitials } from "../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ImageAvatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: "5px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: "#fff",
          background: getAvatarColor(name),
          flexShrink: 0,
        }}
      >
        {getInitials(name)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      style={{
        width: 24,
        height: 24,
        borderRadius: "5px",
        objectFit: "cover",
        flexShrink: 0,
      }}
      onError={() => setFailed(true)}
    />
  );
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const Transactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const { from, to } = useDateFilter();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getManagerTransactions(startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          const data = res?.data?.data;

          setTransactions(data?.transactions || []);
          setSummary(data?.summary || null);
        }
      } catch (err) {
        console.error("Failed to load transactions:", err);
      }
    };

    fetchTransactions();
  }, [from, to]);

  const handleExport = () => {
    if (dataSource.length === 0) {
      message.error("No transactions to export.");
      return;
    }

    const exportData = dataSource.map((row) => ({
      Date: `${row.date} ${row.time}`,
      "Txn ID": row.txnId,
      Driver: row.driver,
      "Vehicle ID": row.vehicleId,
      Vendor: row.vendor,
      "Fuel Type": row.fuelType,
      Volume: row.volume,
      Amount: row.amount,
      Status: row.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    XLSX.writeFile(workbook, "transactions.xlsx");
  };

  const dataSource = transactions.map((txn) => ({
    key: String(txn.id),
    date: txn.date || "-",
    time: txn.time || "-",
    txnId: txn.txn_id || "-",
    driver: txn.driver_name || "-",
    vehicleId: txn.vehicle_id || "-",
    mileage:
      txn.mileage_at_transaction !== null &&
      txn.mileage_at_transaction !== undefined
        ? `${txn.mileage_at_transaction} km`
        : "-",
    fuelLevel: txn.fuel_level ? `${txn.fuel_level} L` : "-",
    vendor: txn.fuel_vendor_name || "-",
    vendorAvatar: resolveAvatarUrl(txn.fuel_vendor_avatar),
    stationId: txn.fuel_vendor_station_id || "-",
    address: txn.fuel_vendor_address || "-",
    fuelType: txn.fuel_type_name || "-",
    volume: txn.volume ? `${txn.volume}L` : "-",
    amount: formatCurrency(txn.amount),
    status: capitalize(txn.status) || "-",
  }));

  const fuelTypeFilters = [
    ...new Set(dataSource.map((item) => item.fuelType)),
  ].map((type) => ({ text: type, value: type }));

  const statusFilters = [...new Set(dataSource.map((item) => item.status))].map(
    (status) => ({ text: status, value: status }),
  );

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.2",
          }}
        >
          <span>{text}</span>
          <span style={{ color: "#71717A" }}>{record.time}</span>
        </div>
      ),
    },
    {
      title: "Txn ID",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: "true",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ImageAvatar src={null} name={text} />
          {text}
        </span>
      ),
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: "true",
    },
    {
      title: "Mileage",
      dataIndex: "mileage",
      key: "mileage",
      sorter: (a, b) =>
        (parseFloat(a.mileage.replace(/[^0-9.]/g, "")) || 0) -
        (parseFloat(b.mileage.replace(/[^0-9.]/g, "")) || 0),
    },
    {
      title: "Fuel Level",
      dataIndex: "fuelLevel",
      key: "fuelLevel",
      sorter: (a, b) =>
        (parseFloat(a.fuelLevel) || 0) - (parseFloat(b.fuelLevel) || 0),
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ImageAvatar src={record.vendorAvatar} name={text} />
          {text}
        </span>
      ),
      enableSearch: "true",
    },
    {
      title: "Station ID",
      dataIndex: "stationId",
      key: "stationId",
      enableSearch: "true",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      enableSearch: "true",
      render: (text) => (
        <Tooltip title={text}>
          <span
            style={{
              maxWidth: 120,
              display: "inline-block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "pointer",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: fuelTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
      render: (type) => <span className="type-badge">{type}</span>,
    },
    {
      title: "Fuel Added",
      dataIndex: "volume",
      key: "volume",
      sorter: (a, b) =>
        (parseFloat(a.volume.replace("L", "")) || 0) -
        (parseFloat(b.volume.replace("L", "")) || 0),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) =>
        (parseFloat(a.amount.replace(/[^0-9.-]+/g, "")) || 0) -
        (parseFloat(b.amount.replace(/[^0-9.-]+/g, "")) || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Approved" ? "approved" : "denied"}`}
        >
          {text}
        </span>
      ),
    },
  ];

  return (
    <div className="transactions-page transaction-manager">
      <div className="date-filter-top">
        <DateFilter />
      </div>
      <div className="stats-container-manager">
        <div className="stats-container-card">
          <GenericStatCard
            title="Total Transactions"
            value={summary?.total_count?.value ?? 0}
            {...(summary?.total_count?.percentage != null && {
              percentage: `${summary.total_count.percentage}`,
            })}
            isIncrease={summary?.total_count?.is_increase}
            icon={garageMoneyIcon}
          />
        </div>

        <div className="stats-container-card">
          <GenericStatCard
            title="Denied"
            value={summary?.denied_count?.value ?? 0}
            {...(summary?.denied_count?.percentage != null && {
              percentage: `${summary.denied_count.percentage}`,
            })}
            isIncrease={summary?.denied_count?.is_increase}
            icon={declinedIcon}
          />
        </div>
        <div className="stats-container-card">
          <GenericStatCard
            title="Total Spend"
            value={formatCurrency(summary?.total_spend?.value)}
            {...(summary?.total_spend?.percentage != null && {
              percentage: `${summary.total_spend.percentage}`,
            })}
            isIncrease={summary?.total_spend?.is_increase}
            icon={paymentIcon}
          />
        </div>
      </div>
      <div className="reports-controls">
        <div className="section-title">Transactions</div>

        <div className="controls-group">
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            className="export-pdf-btn"
            onClick={handleExport}
          >
            Export
          </Button>
        </div>
      </div>
      <div className="transactions-table-container">
        <GenericTable
          dataSource={dataSource}
          columns={columns}
          onRowClick={(r) =>
            navigate("/transaction-manager-details", {
              state: { id: r.key },
            })
          }
          enableRowSelection={false}
          onSelectionChange={(keys, rows) => console.log(keys, rows)}
        />
      </div>
    </div>
  );
};

export default Transactions;
